import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { deleteUser } from "firebase/auth";
import { auth } from "../firebase/config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Save, Key, Trash2, Loader2, AlertTriangle, Volume2, Play, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";
import { useSpeech, getSpeechPrefs, saveSpeechPrefs, AZURE_VOICES, azureAvailable } from "../hooks/useSpeech";

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const AUTOREAD_KEY = "humanizeai_chat_autoread";

export default function Profile() {
  const { user, refresh, logout, changePassword, updateDisplayName } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState(user?.displayName || "");
  const [payments, setP] = useState([]);

  // Voice Settings state
  const { voices, speakText, stop } = useSpeech();
  const englishVoices = voices.filter(v => v.lang?.startsWith("en"));
  const initPrefs = getSpeechPrefs();
  const [voiceRate, setVoiceRate] = useState(initPrefs.rate || 1);
  const [voiceName, setVoiceName] = useState(initPrefs.voiceName || "");
  const [autoReadChat, setAutoReadChat] = useState(() => {
    try { return JSON.parse(localStorage.getItem(AUTOREAD_KEY)) ?? false; } catch { return false; }
  });
  const [highlightReading, setHighlightReading] = useState(initPrefs.highlightReading ?? true);

  // Change password state
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    api.get("/payments/me").then(r => setP(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    try {
      await updateDisplayName(name);
      toast.success("Profile updated");
    } catch {
      toast.error("Failed to update profile");
    }
  };

  const saveVoiceSettings = () => {
    saveSpeechPrefs({ rate: voiceRate, voiceName, highlightReading });
    localStorage.setItem(AUTOREAD_KEY, JSON.stringify(autoReadChat));
    toast.success("Voice settings saved");
  };

  const testVoice = () => {
    stop();
    speakText("Hello! This is how your selected voice and speed sound on HumanizeAI.", voiceRate, voiceName || null);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { toast.error("New passwords do not match"); return; }
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setPwLoading(true);
    try {
      await changePassword(currentPw, newPw);
      toast.success("Password changed successfully");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      const msg = err?.code === "auth/wrong-password" ? "Current password is incorrect"
        : err?.code === "auth/too-many-requests" ? "Too many attempts. Try again later."
        : "Failed to change password";
      toast.error(msg);
    } finally { setPwLoading(false); }
  };

  const deleteAccount = async () => {
    setDeleteLoading(true);
    try {
      // Delete backend data first
      await api.delete("/auth/account");
      // Then delete Firebase Auth user
      if (auth.currentUser) await deleteUser(auth.currentUser);
      toast.success("Account deleted");
      logout?.();
      nav("/");
    } catch (err) {
      if (err?.code === "auth/requires-recent-login") {
        toast.error("Please sign out and sign back in before deleting your account.");
      } else {
        toast.error(err?.response?.data?.detail || "Failed to delete account");
      }
    } finally { setDeleteLoading(false); setShowDeleteModal(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-14">
      <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Account</div>
      <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Your profile</h1>

      {/* Profile info */}
      <div className="mt-8 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 glass-strong rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center text-2xl font-bold">
              {(user?.displayName || user?.email || "?")[0].toUpperCase()}
            </div>
            <div>
              <div className="font-semibold text-lg">{user?.displayName}</div>
              <div className="text-sm text-zinc-400">{user?.email}</div>
            </div>
          </div>
          <div className="mt-6 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Display name</Label>
              <Input data-testid="profile-name" value={name} onChange={e => setName(e.target.value)} className="mt-1.5"/>
            </div>
            <Button onClick={save} className="bg-indigo-600 hover:bg-indigo-700" data-testid="profile-save-btn">
              <Save size={14} className="mr-2"/>Save changes
            </Button>
          </div>
        </div>

        <div className="glass-strong rounded-2xl p-6">
          <div className="text-xs uppercase tracking-widest text-zinc-400">Current plan</div>
          <div className="mt-2 text-2xl font-bold uppercase">{user?.plan}</div>
          {user?.planExpiresAt && (
            <div className="mt-1 text-xs text-zinc-400">
              Plan expires: {new Date(user.planExpiresAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </div>
          )}
          <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
            <div className="glass rounded p-2"><div className="text-[10px] text-zinc-400">Docs scanned</div><div className="font-bold">{user?.totalDocumentsScanned || 0}</div></div>
            <div className="glass rounded p-2"><div className="text-[10px] text-zinc-400">Words processed</div><div className="font-bold">{(user?.totalWordsProcessed || 0).toLocaleString()}</div></div>
          </div>
        </div>
      </div>

      {/* Change password — hidden for Google users */}
      <div className="mt-8 glass-strong rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Key size={16} className="text-indigo-400"/>
          <h2 className="text-lg font-semibold">Change password</h2>
        </div>
        {auth.currentUser?.providerData?.[0]?.providerId === "google.com" ? (
          <p className="text-sm text-zinc-400">Password is managed by Google. Sign in with Google to change it.</p>
        ) : (
          <form onSubmit={handleChangePassword} className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Current password</Label>
              <Input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} className="mt-1.5" required/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">New password</Label>
              <Input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} className="mt-1.5" required/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Confirm new password</Label>
              <Input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className="mt-1.5" required/>
            </div>
            <div className="md:col-span-3">
              <Button type="submit" disabled={pwLoading} className="bg-indigo-600 hover:bg-indigo-700">
                {pwLoading ? <Loader2 size={14} className="mr-2 animate-spin"/> : <Key size={14} className="mr-2"/>}
                Update password
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Payment history */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold">Payment history</h2>
        <div className="mt-4 glass-strong rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[480px] text-sm">
            <thead className="text-xs uppercase tracking-widest text-zinc-400 bg-white/[0.02]">
              <tr>
                <th className="text-left px-5 py-3">Plan</th>
                <th className="text-left px-3 py-3">Amount</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-3 py-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && (
                <tr><td colSpan="4" className="px-5 py-12 text-center text-zinc-500">No payments yet.</td></tr>
              )}
              {payments.map(p => (
                <tr key={p.id} className="border-t border-white/[0.05]">
                  <td className="px-5 py-3 capitalize">{p.plan}</td>
                  <td className="px-3 py-3">R{p.amount} {p.currency}</td>
                  <td className="px-3 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded border ${p.status === 'complete' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : 'bg-amber-500/20 text-amber-300 border-amber-500/40'}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-zinc-400">{(p.createdAt || "").split("T")[0]}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* Voice Settings */}
      <div className="mt-8 glass-strong rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <Volume2 size={16} className="text-indigo-400"/>
          <h2 className="text-lg font-semibold">Voice settings</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Voice selector */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Default voice</Label>
            <select
              value={voiceName}
              onChange={e => setVoiceName(e.target.value)}
              className="mt-1.5 w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500"
            >
              <option value="">System default</option>
              {azureAvailable && (
                <optgroup label="Enhanced HD (Azure)">
                  {AZURE_VOICES.map(v => (
                    <option key={v.name} value={v.name}>{v.label}</option>
                  ))}
                </optgroup>
              )}
              {englishVoices.length > 0 && (
                <optgroup label="Standard (Browser)">
                  {englishVoices.map(v => (
                    <option key={v.name} value={v.name}>{v.name.split("(")[0].trim()}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          {/* Speed */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Default speed — {voiceRate}x</Label>
            <div className="mt-2 flex items-center gap-1 flex-wrap">
              {SPEEDS.map(s => (
                <button
                  key={s}
                  onClick={() => setVoiceRate(s)}
                  className={`text-xs px-2.5 py-1.5 rounded font-mono transition-colors ${voiceRate === s ? "bg-indigo-600 text-white" : "glass text-zinc-400 hover:text-zinc-200"}`}
                >
                  {s}x
                </button>
              ))}
            </div>
          </div>

          {/* Toggles */}
          <div className="space-y-3">
            <button
              onClick={() => setAutoReadChat(p => !p)}
              className="flex items-center gap-2.5 text-sm w-full text-left"
            >
              {autoReadChat ? <ToggleRight size={20} className="text-indigo-400"/> : <ToggleLeft size={20} className="text-zinc-500"/>}
              <span className={autoReadChat ? "text-zinc-200" : "text-zinc-400"}>Auto-read chatbot responses</span>
            </button>
            <button
              onClick={() => setHighlightReading(p => !p)}
              className="flex items-center gap-2.5 text-sm w-full text-left"
            >
              {highlightReading ? <ToggleRight size={20} className="text-indigo-400"/> : <ToggleLeft size={20} className="text-zinc-500"/>}
              <span className={highlightReading ? "text-zinc-200" : "text-zinc-400"}>Highlight sentences while reading</span>
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-end gap-3">
            <Button onClick={testVoice} variant="outline" className="border-white/20 text-zinc-300 hover:text-white hover:border-white/40">
              <Play size={13} className="mr-2"/>Test voice
            </Button>
            <Button onClick={saveVoiceSettings} className="bg-indigo-600 hover:bg-indigo-700">
              <Save size={13} className="mr-2"/>Save settings
            </Button>
          </div>
        </div>
      </div>

      {/* Danger zone */}
      <div className="mt-8 border border-red-500/20 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={16} className="text-red-400"/>
          <h2 className="text-lg font-semibold text-red-400">Danger zone</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-4">Permanently delete your account and all associated data. This cannot be undone.</p>
        <Button
          variant="outline"
          onClick={() => setShowDeleteModal(true)}
          className="border-red-500/40 text-red-400 hover:bg-red-500/10"
          data-testid="delete-account-btn"
        >
          <Trash2 size={14} className="mr-2"/>Delete my account
        </Button>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="w-full max-w-sm glass-strong rounded-2xl p-7 text-center">
            <AlertTriangle size={40} className="mx-auto text-red-400 mb-3"/>
            <h2 className="text-xl font-bold">Delete account?</h2>
            <p className="text-sm text-zinc-400 mt-2">
              This will permanently delete your account, all documents, scan history, and payment records. There is no going back.
            </p>
            <div className="mt-6 flex gap-3">
              <Button
                variant="ghost"
                onClick={() => setShowDeleteModal(false)}
                className="flex-1"
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={deleteAccount}
                disabled={deleteLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                {deleteLoading ? <Loader2 size={14} className="animate-spin"/> : "Yes, delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
